/**
 * Host detection helpers.
 *
 * HIBANE Books (Finance Desk) now lives in a separate Lovable project served
 * at https://books.hibane.ai. This project no longer hosts any Books UI;
 * `financeUrl()` always returns an absolute URL pointing to the standalone
 * Books app.
 *
 * desk.hibane.ai / hr.hibane.ai are not yet active for routing.
 */

export const DESK_HOST = 'desk.hibane.ai';
export const HR_HOST = 'hr.hibane.ai';
// Internal product key remains "finance" for DB compatibility, but the host
// is the public Books domain.
export const FINANCE_HOST = 'books.hibane.ai';
const MARKETING_HOSTS = new Set(['hibane.ai', 'www.hibane.ai']);

const normalizeHostname = (hostname: string): string => {
  return hostname.trim().toLowerCase().replace(/\.$/, '');
};

const getHostname = (): string => {
  if (typeof window === 'undefined') return '';
  return normalizeHostname(window.location.hostname);
};

// Desk/HR subdomain routing remains disabled until ready.
export const isDeskHost = (): boolean => false;
export const isHrHost = (): boolean => false;

export const isMarketingHost = (): boolean => MARKETING_HOSTS.has(getHostname());

/**
 * True for the Lovable editor preview, the published *.lovable.app domain,
 * or local development.
 */
export const isPreviewOrDevHost = (): boolean => {
  const h = getHostname();
  if (!h) return true;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h.endsWith('.lovable.app') ||
    h.endsWith('.lovableproject.com') ||
    h.endsWith('.lovable.dev')
  );
};

export const deskUrl = (path: string = '/admin/dashboard'): string => {
  return path.startsWith('/') ? path : `/${path}`;
};

/**
 * Always returns an absolute URL to the standalone HR Desk
 * (https://hr.hibane.ai). The legacy `/hr/...` prefix is stripped.
 */
export const hrUrl = (path: string = '/dashboard'): string => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const stripped = normalized.replace(/^\/hr(?=\/|$)/, '') || '/';
  return `https://${HR_HOST}${stripped}`;
};

/**
 * Always returns an absolute URL to the standalone HIBANE Books app
 * (https://books.hibane.ai). The legacy `/finance/...` prefix is stripped.
 */
export const financeUrl = (path: string = '/dashboard'): string => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const stripped = normalized.replace(/^\/finance(?=\/|$)/, '') || '/';
  return `https://${FINANCE_HOST}${stripped}`;
};
